"use client";

import React, { useContext, useId } from "react";
import { Select as AntSelect } from "antd";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";

type Option = {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
};

type SelectProps = {
  label?: string;
  name?: string;
  value?: string;
  onChange: (value: string, option: Option) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  labelClassName?: string;
  hideLabel?: boolean;
  emptyMessage?: string;
};

const Select: React.FC<SelectProps> = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  className = "",
  required = false,
  disabled = false,
  labelClassName = "",
  hideLabel = false,
  emptyMessage = "No data to select",
}) => {
  const { theme } = useContext(CMSModal);
  const id = useId();

  const isDisabled = disabled || options.length === 0;

  return (
    <div className="flex flex-col space-y-1">
      {label && (
        <label
          htmlFor={id}
          className={twMerge(
            hideLabel ? "sr-only" : "text-xs",
            labelClassName,
            theme === "dark" ? "text-gray-300" : "text-gray-700",
          )}
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <AntSelect
        id={id}
        value={value || undefined}
        disabled={isDisabled}
        placeholder={options.length === 0 ? emptyMessage : placeholder}
        className={twMerge("w-full", className)}
        options={options.map((opt) => ({
          value: opt.value,
          disabled: opt.disabled,
          label: (
            <div className="flex items-center gap-2 text-sm mt-[3px] text-gray-600">
              {opt.icon}
              {opt.label}
            </div>
          ),
        }))}
        onChange={(val) => {
          const selected = options.find((o) => o.value === val);
          if (selected) onChange(val, selected);
        }}
      />
    </div>
  );
};

export default Select;
